import {PopperConfiguration,
        PopperPlacement,
        PoppableElementConfig,
        PopperOnDisplayAnimation} from './popper-configuration';
import { TippyInstance } from './interfaces/tippy-instance';
declare let tippy;

export let PoppableElementFactory = {

  /**
   * Generates a configuration object based upon some initial configurations applied to the tooltip.
   * @param defaultPlacement - the default placement of the popover, the start and end is relative to its reference elements bounding rectangle
   * @param triggerAction - what triggers the popover to be open? default action is click.
   * @param customStyleClassName - custom classes can be added to the popover to style it based upon your needs, be wary that the class name will have '-theme' appended to it.
   * @param htmlTemplateReference - a container for the popover and tooltip innerHTML. This will ensure that your event listeners are preserved when this container is used.
   */

  generateTooltipConfig: function (
    defaultPlacement: PopperPlacement,
    triggerAction: string,
    htmlReference: HTMLElement,
    customStyleClassName: string,
    showHideDuration: [number, number] = [420, 0]): PoppableElementConfig {

    let showArrow = false;
    let appendTo = this.getScopeContainer();
    let stickToReference = false;
    let zIndex = 9999;
    let interactive = false;
    // Changed from perspective to fade to fix 3D redraw styling issue in Firefox
    let displayAnimation: PopperOnDisplayAnimation = 'fade';
    let popperOptions = {
      modifiers: {
        preventOverflow: {
          enabled: true
        },
        computeStyle: {
          gpuAcceleration: false
        },
        hide: {
          enabled: false
        },
        removeOnDestroy: true
      }
    };
    let beforeShownCallback = (instance: any): void => {
      let content = instance.popper.querySelector('.tippy-content');
      if (content != null) {
        let innerHTML = content.innerHTML;
        if (innerHTML.trim() === '' && instance.popperInstance != null) {
          instance.popperInstance.destroy();
        }
      }
    };

    return PopperConfiguration.getPopperConfiguration({
      showArrow: showArrow,
      defaultPlacement: defaultPlacement,
      trigger: triggerAction,
      appendTo: appendTo,
      stickToReference: stickToReference,
      interactive: interactive,
      onDisplayAnimation: displayAnimation,
      customStyleClassName: customStyleClassName,
      htmlReference: htmlReference,
      popperJsOptions: popperOptions,
      zIndex: zIndex,
      transitionDuration: showHideDuration,
      beforeShownCallback: beforeShownCallback
    });
  },

  /**
   * Generates a configuration object based upon some initial configurations applied to the popover.
   * @param defaultPlacement - the default placement of the popover, the start and end is relative to its reference elements bounding rectangle
   * @param triggerAction - what triggers the popover to be open? default action is click.
   * @param customStyleClassName - custom classes can be added to the popover to style it based upon your needs, be wary that the class name will have '-theme' appended to it.
   * @param htmlTemplateReference - a container for the popover and tooltip innerHTML. This will ensure that your event listeners are preserved when this container is used.
   * @param zIndex - a configurable z-index property, by default it displays underneath the app head, which has a z-index of 6000.
   * @param onHiddenCallback - a callback to be called when the popovers are hidden
   * @param onShownCallback - a callback to be called when the popovers are shown
   * @param panelPlacementLocator - an html reference element that serves as a locator to where the html for the panel will be placed.
   */

  generatePopoverConfig: function(
    showArrow: boolean,
    defaultPlacement: PopperPlacement,
    zIndex: number,
    customStyleClassName: string,
    htmlReference: HTMLElement,
    onHiddenCallback: (instance: any) => void,
    onShownCallback: (instance: any) => void,
    panelPlacementLocator?: HTMLElement): PoppableElementConfig {
      if (panelPlacementLocator == null) {
        panelPlacementLocator = this.getScopeContainer();
      }
      let appendTo = panelPlacementLocator;
      let stickToReference = false;
      let interactive = true;
      let arrowSize = 'scale(2)';
      let triggerAction = 'click';
      let displayAnimation: PopperOnDisplayAnimation = 'shift-toward';
      let popperOptions = {
      modifiers: {
        preventOverflow: {
          enabled: true,
          priority: ['right', 'left'],
          boundariesElement: document.body
        },
        computeStyle: {
          gpuAcceleration: false
        },
        hide: {
          enabled: false
        }
      }
    };

      return PopperConfiguration.getPopperConfiguration({
      showArrow: showArrow,
      defaultPlacement: defaultPlacement,
      trigger: triggerAction,
      appendTo: appendTo,
      stickToReference: stickToReference,
      interactive: interactive,
      arrowSizeTransform: arrowSize,
      onDisplayAnimation: displayAnimation,
      customStyleClassName: customStyleClassName,
      htmlReference: htmlReference,
      popperJsOptions: popperOptions,
      zIndex: zIndex,
      //PDS-1263 make the popper close on scroll
      //PDS-1420 the close on scroll created more issues with nested popovers
      onShownCallback: onShownCallback,
      onHiddenCallback: onHiddenCallback
    });

  },

  /**
   * Generate the poppable element and return an instance of it.
   * @param trigger: A poppable element like a tooltip, or popover needs a reference. The trigger that opens it.
   * @param config: poppover element config.
   * @param scopeClass: pass in the polymer component name here so it can be scoped to the widget's embedded styles.
   */
  generatePoppableElement: function(trigger: string | HTMLElement, config: PoppableElementConfig, scopeClass?: string): TippyInstance | undefined {
    if (window.hasOwnProperty('tippy')) {
      tippy.disableAnimations();
      let tippyInstance: TippyInstance | undefined = tippy.one(trigger, config);
      if (tippyInstance != null &&
          tippyInstance.popper != null &&
          scopeClass != null &&
          tippyInstance.popper.firstChild instanceof HTMLElement) {
        tippyInstance.popper.classList.add(scopeClass);
        (<HTMLElement>tippyInstance.popper.firstChild).classList.add(scopeClass);
      }
      return tippyInstance;
    } else {
      console.error('tippy is undefined. Did the tippy.js script get loaded before calling this? - poppable-element-factory.ts');
    }
  },
  // PDS-Scope PDS-313 needs a wrapper around the tippy-popper element.
  getScopeContainer: function(): HTMLElement {
    let tippyScopeExists = document.getElementById('pds-tippy-scope');
    if (tippyScopeExists) {
      return tippyScopeExists;
    }
    let pdsScopeDiv = document.createElement('div');
    pdsScopeDiv.classList.add('pds-scope');
    pdsScopeDiv.setAttribute('id', 'pds-tippy-scope');
    document.body.appendChild(pdsScopeDiv);
    return pdsScopeDiv;
  }

};
